import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/environment.mjs';
import PageShell from '../../components/PageShell.mjs';
import { AuthContext } from '../../authProvider.mjs';

/**
 * Choose who a claim is for, then fill it in with the ORDINARY claim form.
 *
 * This page only picks. The first version had its own cut-down form, and that
 * was the mistake: it lacked everything the real forms do - city search and
 * published rates, seasonal lookups, quick fill, meals and lodging handling,
 * receipt upload and extraction, exchange rates - so a claim filed for someone
 * was a poorer claim than one they would have filed themselves.
 *
 *   Per diem  -> the normal create form, in "on behalf of" mode. It behaves
 *                exactly as it does for the claimant; only its final submit goes
 *                to the on-behalf endpoint, so the claim is theirs.
 *   Expense   -> a draft is opened in the claimant's name first (receipts have to
 *                attach to a claim), then the normal expense form edits it.
 */
const API_URL = getApiBaseUrl();

const FileClaimForUser = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [person, setPerson] = useState(null);
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  // Expense drafts this admin started for someone and has not submitted.
  const [drafts, setDrafts] = useState([]);
  const [discardingId, setDiscardingId] = useState(null);

  const searchTimer = useRef(null);

  // People in the admin's own region. Scoped server-side, so the picker cannot
  // offer anyone they may not file for.
  useEffect(() => {
    if (person || personQuery.trim().length < 2) { setPersonResults([]); return undefined; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/admin-action/users/search`, {
          params: { q: personQuery.trim() },
          withCredentials: true,
        });
        // { success, data: { users, region } } - reading data.data alone would
        // hand back an object, which has no .map, and the list would stay empty.
        const found = res.data?.data?.users;
        // Never yourself: your own claims go through File a Claim, and the
        // server refuses an on-behalf claim for the person filing it.
        setPersonResults(Array.isArray(found)
          ? found.filter(u => String(u.id) !== String(user?.id))
          : []);
      } catch {
        setPersonResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [personQuery, person, user?.id]);

  const loadDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/claims/on-behalf/drafts`, {
        withCredentials: true,
      });
      setDrafts(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setDrafts([]);
    }
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const startPerDiem = () => {
    // Carried in navigation state: the form reads it, and a reload without it
    // lands back here rather than on a form that does not know whose it is.
    navigate('/perdiem/create/on-behalf', { state: { onBehalfOf: person } });
  };

  const startExpense = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/admin/claims/expense/on-behalf/start`,
        { userId: person.id },
        { withCredentials: true }
      );
      const claimId = res.data?.data?.claimId;
      if (!claimId) throw new Error('The claim could not be started.');
      // The ordinary expense form, on a draft that already belongs to them.
      navigate(`/claim/expense/${claimId}`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to start the claim');
    } finally {
      setStarting(false);
    }
  };

  const discardDraft = async (claimId) => {
    if (!window.confirm('Discard this draft? The claimant never saw it, and it cannot be recovered.')) return;
    setDiscardingId(claimId);
    try {
      await axios.delete(`${API_URL}/expenses/claim/${claimId}`, { withCredentials: true });
      await loadDrafts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to discard the draft');
    } finally {
      setDiscardingId(null);
    }
  };

  return (
    <PageShell
      title="File a Claim for Someone"
      photo={false}
      subtitle="Choose the person, then fill the claim in on the normal claim form."
      width="3xl"
    >
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <p className="text-sm text-gray-600">
            It will be their claim: it appears in their dashboard, follows the normal route,
            and they are emailed when you submit it. You can only file for people in your own region,
            and not for yourself - file your own claims from File a Claim on the home page.
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Unfinished expense claims - so one left half-done is picked up, not
            forgotten in someone else's name. */}
        {drafts.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="font-semibold text-gray-800 mb-1">Your unfinished claims</h2>
            <p className="text-xs text-gray-500 mb-3">
              Expense claims you started for someone and have not submitted. They are
              hidden from the claimant until you do.
            </p>
            <ul className="divide-y divide-gray-100">
              {drafts.map(d => (
                <li key={d.claimId} className="py-3 sm:py-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-gray-800 min-w-0 break-words">
                    <strong>{d.claimant?.name || 'Unknown'}</strong>
                    <span className="text-gray-500">
                      {' '}· expense · ${(Number(d.totalUsdAmount) || 0).toFixed(2)}
                      {' '}· last saved {new Date(d.updatedAt).toLocaleString()}
                    </span>
                  </span>
                  <span className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => navigate(`/claim/expense/${d.claimId}`)}
                      className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs bg-stanford-red text-white rounded hover:bg-stanford-light-red"
                    >
                      Continue
                    </button>
                    <button
                      onClick={() => discardDraft(d.claimId)}
                      disabled={discardingId === d.claimId}
                      className="flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      {discardingId === d.claimId ? 'Discarding…' : 'Discard'}
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 1 - who */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="font-semibold text-gray-800 mb-3">1. Who is this claim for?</h2>
          {person ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3">
              <span className="text-sm text-gray-800 min-w-0 break-words">
                <strong>{person.firstName} {person.lastName}</strong>
                <span className="text-gray-600">
                  {' '}· {person.email}{person.region ? ` · ${person.region}` : ''}
                </span>
              </span>
              <button
                onClick={() => { setPerson(null); setPersonQuery(''); }}
                className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm sm:text-xs border border-gray-300 text-gray-700 rounded hover:bg-white"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={personQuery}
                onChange={(e) => setPersonQuery(e.target.value)}
                placeholder="Search your region by name or email…"
                className="w-full min-h-[44px] sm:min-h-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-stanford-red"
              />
              {searching && <p className="text-xs text-gray-500 mt-1">Searching…</p>}
              {personResults.length > 0 && (
                <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                  {personResults.map(u => (
                    <li key={u.id}>
                      <button
                        onClick={() => { setPerson(u); setPersonResults([]); }}
                        className="w-full min-h-[44px] sm:min-h-0 text-left px-3 py-2 text-sm hover:bg-gray-50 break-words"
                      >
                        {u.firstName} {u.lastName}
                        <span className="text-gray-500"> · {u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!searching && personQuery.trim().length >= 2 && personResults.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">Nobody in your region matches that.</p>
              )}
            </>
          )}
        </div>

        {/* 2 - which form */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <h2 className="font-semibold text-gray-800 mb-1">2. Which claim?</h2>
          <p className="text-xs text-gray-500 mb-3">
            Opens the normal claim form, filled in as {person ? person.firstName : 'them'}.
          </p>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            <button
              onClick={startPerDiem}
              disabled={!person || starting}
              className="min-h-[44px] sm:min-h-0 px-3 sm:px-5 py-2.5 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-40 font-medium"
            >
              Per diem claim
            </button>
            <button
              onClick={startExpense}
              disabled={!person || starting}
              className="min-h-[44px] sm:min-h-0 px-3 sm:px-5 py-2.5 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-40 font-medium"
            >
              {starting ? 'Opening…' : 'Expense claim'}
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="col-span-2 min-h-[44px] sm:min-h-0 px-5 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
    </PageShell>
  );
};

export default FileClaimForUser;
